import {useUnlinkBenefitFromRawTransactionMutation} from '@transactions/graphql';
import {parseDate} from 'common/date';
import * as React from 'react';
import {Button} from 'src/Button';
import {formatCents} from 'src/CardBenefitsView/benefitUtils';
import {Modal} from 'src/Modal';
import type {ApolloCache} from '@apollo/client';
import type {SystemProps} from 'src/Modals';

// Linking/unlinking changes per-period usedAmount/remainingAmount and
// linked-row lists inside cached fee periods, which the BasicRawTransaction
// mutation payload cannot update. Evict the fee period queries so the
// benefits pages refetch instead of showing stale aggregates.
export const evictFeePeriodQueries = (cache: ApolloCache<unknown>) => {
  cache.evict({fieldName: 'allFeePeriods'});
  cache.evict({fieldName: 'feePeriodsByAccount'});
  cache.gc();
};

export type UnlinkBenefitTransactionModalUserProps = {
  rawTransaction: {
    _id: string;
    amount: number;
    date: string;
    description: string;
  };
  /** Shown in the confirmation copy when the caller knows the benefit */
  benefitName?: string | null;
  onUnlinked?: () => void;
};

/**
 * Confirmation before unlinking a raw transaction from its benefit. The
 * transaction itself is untouched — it just stops counting toward the
 * benefit's periods.
 */
export const UnlinkBenefitTransactionModal = (
  props: UnlinkBenefitTransactionModalUserProps & SystemProps,
) => {
  const {rawTransaction, benefitName, onRequestClose, onUnlinked} = props;
  const [unlinkBenefit, {loading}] = useUnlinkBenefitFromRawTransactionMutation(
    {update: evictFeePeriodQueries},
  );
  const [error, setError] = React.useState<string | null>(null);

  const handleUnlink = async () => {
    setError(null);
    try {
      await unlinkBenefit({variables: {rawTransactionId: rawTransaction._id}});
      onUnlinked?.();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="destructive"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={handleUnlink}
        disabled={loading}
      >
        {loading ? 'Unlinking...' : 'Unlink'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Unlink transaction"
      footer={footer}
      size="sm"
    >
      {error && (
        <div className="bg-destructive/10 text-destructive mb-3 rounded-md p-3 text-sm">
          {error}
        </div>
      )}
      <p>
        Are you sure you want to unlink{' '}
        <strong>{rawTransaction.description}</strong> (
        {parseDate(rawTransaction.date).toLocaleString({
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}{' '}
        &middot; {formatCents(rawTransaction.amount)}) from{' '}
        {benefitName ? <strong>{benefitName}</strong> : 'its benefit'}? The
        transaction is not deleted — it just stops counting toward the
        benefit.
      </p>
    </Modal>
  );
};
